/**
 * Lights Canada — Delivery ETA Widget
 *
 * Detects the customer's province via ipinfo.io and renders a region-specific
 * delivery date range on product pages, e.g.
 *
 *     ⊙ Estimated delivery to Nepean, ON : Apr 5th - Apr 25th
 *
 * The city in the rendered line is a clickable button — opens a small popup
 * where the customer can enter a postal code to override the IP-detected
 * location. The postal is resolved against fsa-cities.json (lazy-loaded).
 *
 * Source of truth for transit times: pages/shipping-eta (the eta.html chart).
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * INTEGRATION (Shopify)
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * 1. Get a free ipinfo.io token (50K req/month) and paste it into IPINFO_TOKEN
 *    below. The unauthenticated endpoint will work too, but it rate-limits per
 *    IP and silently degrades on busy days — not recommended for production.
 *
 * 2. Upload this file to your theme's `assets/` folder.
 *
 * 3. In `layout/theme.liquid`, add this to <head> so the lookup fires on every
 *    page (so the answer is in localStorage before the customer reaches a PDP):
 *
 *      <script src="{{ 'delivery-eta.js' | asset_url }}" defer></script>
 *
 * 4. On the product template (e.g. `sections/main-product.liquid`), drop in
 *    the placeholder wherever you want the line to appear:
 *
 *      <div class="lc-delivery-eta" data-brand="{{ product.vendor | escape }}"></div>
 *
 *    The script auto-discovers any `.lc-delivery-eta` element on the page,
 *    reads its `data-brand`, and fills the message in.
 *
 * 5. Suggested CSS (paste into your theme stylesheet):
 *
 *      .lc-delivery-eta {
 *        display: inline-flex; align-items: center; gap: .5rem;
 *        font-size: .95rem; color: #333;
 *      }
 *      .lc-delivery-eta[hidden] { display: none; }
 *      .lc-delivery-eta__icon { display: inline-flex; flex: none; }
 *      .lc-delivery-eta__location { font-weight: 600; text-decoration: underline; }
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * MAINTENANCE
 * ─────────────────────────────────────────────────────────────────────────────
 *   - When eta.html changes, update BRAND_TRANSIT.
 *   - If a Shopify vendor name doesn't match an eta.html brand, add an alias
 *     to BRAND_ALIASES (both keys + values lowercased).
 *   - To force-invalidate every cached location, bump the version in CACHE_KEY.
 */

(function () {
  'use strict';

  // ===========================================================================
  // CONFIG
  // ===========================================================================
  var IPINFO_TOKEN = 'b253def6df0e23';                            // paste your ipinfo.io token here
  var CACHE_KEY    = 'lc_delivery_location_v1';     // bump version to invalidate
  var CACHE_TTL_MS = 7 * 24 * 60 * 60 * 1000;       // 7 days
  var OVERRIDE_KEY = 'lc_delivery_override_v1';     // user-entered postal code, no TTL
  var FSA_ASSET    = 'fsa-cities.json';             // theme asset filename for the FSA→city table

  // ---------------------------------------------------------------------------
  // TEST MODE
  // ---------------------------------------------------------------------------
  // When TEST_MODE is true AND the detected country is NOT Canada (e.g. you're
  // QAing from outside CA), the script swaps in a rotating entry from
  // TEST_LOCATIONS so each region branch (west / central / east / territory)
  // can be exercised across consecutive page loads. The rotation index is
  // persisted in sessionStorage and advances once per page load.
  //
  // Real Canadian visitors are never overridden. Set TEST_MODE = false before
  // shipping to production.
  var TEST_MODE = false;
  var TEST_LOCATIONS = [
    { country: 'CA', provinceCode: 'ON', city: 'Nepean'     }, // central
    { country: 'CA', provinceCode: 'BC', city: 'Vancouver'  }, // west
    { country: 'CA', provinceCode: 'NS', city: 'Halifax'    }, // east
    { country: 'CA', provinceCode: 'YT', city: 'Whitehorse' }  // territory
  ];

  // Vendor-name overrides for cases where Shopify's product.vendor doesn't
  // match an eta.html brand exactly. Both keys and values lowercased.
  var BRAND_ALIASES = {
    // 'wac':            'wac lighting',
    // 'visual comfort': 'visual comfort signature collection',
  };

  // Province code → region key
  var PROVINCE_REGION = {
    BC: 'west',     AB: 'west',     SK: 'west',
    MB: 'central',  ON: 'central',  QC: 'central',
    NB: 'east',     NL: 'east',     NS: 'east',     PE: 'east',
    YT: 'territory', NT: 'territory', NU: 'territory'
  };

  // ipinfo returns the full English province name in the `region` field.
  // Keys are lowercased so the lookup is case-tolerant; we also accept the
  // ISO 2-letter code directly via resolveProvinceCode().
  var PROVINCE_NAME_TO_CODE = {
    'alberta':                   'AB',
    'british columbia':          'BC',
    'manitoba':                  'MB',
    'new brunswick':             'NB',
    'newfoundland and labrador': 'NL',
    'newfoundland':              'NL',
    'nova scotia':               'NS',
    'northwest territories':     'NT',
    'nunavut':                   'NU',
    'ontario':                   'ON',
    'prince edward island':      'PE',
    'quebec':                    'QC',
    'québec':                    'QC',
    'saskatchewan':              'SK',
    'yukon':                     'YT',
    'yukon territory':           'YT'
  };

  // Tolerant resolver: handles case variations, alt names, and the (rare)
  // case where ipinfo returns the 2-letter code instead of the full name.
  function resolveProvinceCode(region) {
    if (!region) return null;
    var raw = String(region).trim();
    var upper = raw.toUpperCase();
    if (upper.length === 2 && PROVINCE_REGION[upper]) return upper;
    return PROVINCE_NAME_TO_CODE[raw.toLowerCase()] || null;
  }

  // Brand → {region: [min, max]} TOTAL business days (processing + transit),
  // straight from the eta.html chart. null means "Made to order — contact us".
  var BRAND_TRANSIT = {
    'acclaim':                                 { west:[5,8],   central:[5,8],   east:[8,12] },
    'adesso':                                  { west:[15,19], central:[13,17], east:[15,19] },
    'alora':                                   { west:[3,6],   central:[5,8],   east:[7,10] },
    'alora mood':                              { west:[4,6],   central:[5,8],   east:[7,10] },
    'artcraft lighting':                       { west:[5,8],   central:[4,6],   east:[5,8] },
    'artika':                                  { west:[4,6],   central:[3,6],   east:[5,8] },
    'avista':                                  { west:[6,10],  central:[4,8],   east:[6,10] },
    'canarm':                                  { west:[4,7],   central:[3,6],   east:[5,8] },
    'capital lighting':                        { west:[15,19], central:[15,19], east:[17,21] },
    'classy caps':                             { west:[5,8],   central:[3,6],   east:[5,8] },
    'corbett':                                 { west:[15,19], central:[15,19], east:[17,21] },
    'cwi':                                     { west:[5,8],   central:[3,6],   east:[5,8] },
    'dainolite':                               { west:[6,10],  central:[4,8],   east:[6,10] },
    'dals':                                    { west:[5,8],   central:[3,6],   east:[5,8] },
    'dvi':                                     { west:[5,8],   central:[3,6],   east:[5,8] },
    'dweled':                                  { west:[15,19], central:[15,19], east:[17,21] },
    'eglo':                                    { west:[5,8],   central:[3,6],   east:[5,8] },
    'elan':                                    { west:[6,10],  central:[6,10],  east:[8,12] },
    'et2':                                     { west:[6,10],  central:[6,10],  east:[8,12] },
    'eurofase':                                { west:[5,8],   central:[3,6],   east:[5,8] },
    'fine art handcrafted lighting':           null,
    'flow decor':                              { west:[8,14],  central:[6,12],  east:[6,12] },
    'fredrick ramond':                         { west:[6,10],  central:[6,10],  east:[8,12] },
    'galaxy lighting':                         { west:[4,6],   central:[5,8],   east:[7,10] },
    'generation lighting':                     { west:[6,10],  central:[6,10],  east:[8,12] },
    'hinkley':                                 { west:[6,10],  central:[6,10],  east:[8,12] },
    'hubbardton forge':                        null,
    'hudson valley lighting':                  { west:[15,19], central:[15,19], east:[17,21] },
    'hunter':                                  { west:[4,6],   central:[3,6],   east:[5,8] },
    'kendal lighting':                         { west:[4,6],   central:[5,8],   east:[7,10] },
    'kichler':                                 { west:[6,10],  central:[6,10],  east:[8,12] },
    'kuzco':                                   { west:[4,6],   central:[5,8],   east:[7,10] },
    'lark':                                    { west:[6,10],  central:[6,10],  east:[8,12] },
    'legrand':                                 { west:[5,8],   central:[3,6],   east:[5,8] },
    'litup lighting':                          { west:[5,8],   central:[3,6],   east:[5,8] },
    'luce lumen':                              { west:[5,10],  central:[3,8],   east:[5,10] },
    'matteo':                                  { west:[7,10],  central:[8,12],  east:[10,14] },
    'maxilite':                                { west:[3,6],   central:[4,6],   east:[7,10] },
    'maxim lighting':                          { west:[6,10],  central:[6,10],  east:[8,12] },
    'mercana':                                 { west:[4,6],   central:[5,8],   east:[7,10] },
    'meridian':                                { west:[15,19], central:[15,19], east:[17,21] },
    'mitzi':                                   { west:[15,19], central:[15,19], east:[17,21] },
    'modern forms':                            { west:[15,19], central:[15,19], east:[17,21] },
    'page one':                                { west:[4,8],   central:[5,8],   east:[8,12] },
    'progress lighting':                       { west:[6,10],  central:[6,10],  east:[8,12] },
    'quoizel':                                 { west:[15,19], central:[15,19], east:[17,21] },
    'renwil':                                  { west:[4,6],   central:[3,6],   east:[5,8] },
    'russell lighting':                        { west:[3,6],   central:[4,6],   east:[7,10] },
    'savoy house':                             { west:[15,19], central:[15,19], east:[17,21] },
    'schonbek':                                null,
    'sonneman':                                { west:[11,15], central:[11,15], east:[13,17] },
    'starfire':                                { west:[4,6],   central:[3,6],   east:[5,8] },
    'studio m':                                { west:[4,8],   central:[6,10],  east:[8,12] },
    'thesis':                                  { west:[5,8],   central:[3,6],   east:[5,8] },
    'trend':                                   { west:[4,8],   central:[5,8],   east:[8,12] },
    'trio lighting':                           { west:[12,19], central:[13,17], east:[15,19] },
    'troy':                                    { west:[14,17], central:[15,19], east:[17,21] },
    'tubicen':                                 { west:[4,8],   central:[5,8],   east:[8,12] },
    'umbra':                                   { west:[6,10],  central:[5,8],   east:[6,10] },
    'visual comfort architectural collection': { west:[6,10],  central:[6,10],  east:[8,12] },
    'visual comfort fan collection':           { west:[6,10],  central:[6,10],  east:[8,12] },
    'visual comfort modern collection':        { west:[6,10],  central:[6,10],  east:[8,12] },
    'visual comfort signature collection':     { west:[14,22], central:[14,22], east:[14,22] },
    'visual comfort studio collection':        { west:[6,10],  central:[6,10],  east:[8,12] },
    'wac lighting':                            { west:[15,19], central:[15,19], east:[17,21] },
    'z-lite':                                  { west:[5,8],   central:[3,6],   east:[5,8] }
  };

  var MONTH_SHORT = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  var PIN_SVG =
    '<svg viewBox="0 0 24 24" width="16" height="16" fill="none" ' +
    'stroke="currentColor" stroke-width="1.75" aria-hidden="true">' +
    '<circle cx="12" cy="12" r="9"/>' +
    '<circle cx="12" cy="12" r="2.25" fill="currentColor" stroke="none"/>' +
    '</svg>';

  // Tracked here so renderInto() can detect an orphaned popup (anchor about
  // to be re-rendered) and close it before innerHTML rebuild detaches it.
  var openPopup = null;

  // ===========================================================================
  // LOCATION (cached + fetched once per session)
  // ===========================================================================
  function readCache() {
    try {
      var raw = localStorage.getItem(CACHE_KEY);
      if (!raw) return null;
      var data = JSON.parse(raw);
      if (!data || typeof data.fetchedAt !== 'number') return null;
      if (Date.now() - data.fetchedAt > CACHE_TTL_MS) return null;
      return data;
    } catch (_) { return null; }
  }

  function writeCache(data) {
    try {
      data.fetchedAt = Date.now();
      localStorage.setItem(CACHE_KEY, JSON.stringify(data));
    } catch (_) { /* private mode / quota — ignore */ }
  }

  // User-entered postal-code override. Stored without an expiry — the customer
  // explicitly picked this, so we keep it until they clear it via the popup's
  // "Use my location" link.
  function readOverride() {
    try {
      var raw = localStorage.getItem(OVERRIDE_KEY);
      if (!raw) return null;
      var data = JSON.parse(raw);
      if (!data || !data.provinceCode) return null;
      return { country: 'CA', provinceCode: data.provinceCode, city: data.city || '', postalFSA: data.postalFSA || '' };
    } catch (_) { return null; }
  }

  function writeOverride(data) {
    try { localStorage.setItem(OVERRIDE_KEY, JSON.stringify(data)); } catch (_) {}
  }

  function clearOverride() {
    try { localStorage.removeItem(OVERRIDE_KEY); } catch (_) {}
  }

  function fetchLocation() {
    var url = 'https://ipinfo.io/json' +
              (IPINFO_TOKEN ? '?token=' + encodeURIComponent(IPINFO_TOKEN) : '');
    return fetch(url, { cache: 'no-store' })
      .then(function (r) {
        if (!r.ok) throw new Error('ipinfo HTTP ' + r.status);
        return r.json();
      })
      .then(function (j) {
        var country = j.country || '';
        var regionName = j.region || '';
        var provinceCode = country === 'CA' ? resolveProvinceCode(regionName) : null;
        return {
          country: country,
          provinceCode: provinceCode,
          city: j.city || ''
        };
      });
  }

  // Advance the test rotation once per page load, regardless of how many
  // .lc-delivery-eta elements end up on the page or how many times render()
  // is called (e.g. quickview re-opens). Keyed in sessionStorage so the
  // rotation persists across navigations within the same tab.
  var TEST_INDEX_KEY = 'lc_delivery_test_index';
  var testRotationIndex = (function () {
    if (!TEST_MODE) return 0;
    try {
      var cur = parseInt(sessionStorage.getItem(TEST_INDEX_KEY) || '0', 10) || 0;
      cur = cur % TEST_LOCATIONS.length;
      sessionStorage.setItem(TEST_INDEX_KEY, String((cur + 1) % TEST_LOCATIONS.length));
      return cur;
    } catch (_) { return 0; }
  })();

  var testLoggedOnce = false;
  function maybeOverrideForTest(loc) {
    if (!TEST_MODE) return loc;
    if (loc && loc.country === 'CA') return loc; // never override real CA visitors
    var pick = TEST_LOCATIONS[testRotationIndex];
    try {
      if (!testLoggedOnce && window.console && console.info) {
        console.info('[LC Delivery ETA] TEST_MODE active — using', pick);
        testLoggedOnce = true;
      }
    } catch (_) {}
    return pick;
  }

  // The override is read fresh on every call (so clearing/changing it in the
  // popup is reflected on the next render without manual invalidation). The
  // IP fetch is memoized via ipLocationPromise — that part only runs once.
  //
  // When an override has a saved FSA, we re-resolve the city against the
  // current FSA table on every page load. That way, post-deploy table
  // corrections (e.g. fixing "H9R: Montreal" -> "H9R: Pointe-Claire")
  // propagate to existing customers without forcing them to re-enter their
  // postal code. The fetch is browser-cached after the first hit.
  var ipLocationPromise = null;
  function getLocation() {
    var override = readOverride();
    if (override && override.postalFSA) {
      return loadFsaTable().then(function (table) {
        var entry = table && table[override.postalFSA];
        if (entry && entry.province) {
          return {
            country: 'CA',
            provinceCode: entry.province,
            city: entry.city || '',
            postalFSA: override.postalFSA
          };
        }
        return override;
      });
    }
    if (override) return Promise.resolve(override);
    if (ipLocationPromise) return ipLocationPromise;
    var cached = readCache();
    if (cached) {
      ipLocationPromise = Promise.resolve(cached);
      return ipLocationPromise;
    }
    ipLocationPromise = fetchLocation()
      .then(function (loc) { writeCache(loc); return loc; })
      .catch(function () {
        // Don't poison the cache on failure — let the next page retry.
        ipLocationPromise = null;
        return null;
      });
    return ipLocationPromise;
  }

  // ===========================================================================
  // BUSINESS DAY MATH
  // ===========================================================================
  function isWeekend(d) {
    var day = d.getDay();
    return day === 0 || day === 6;
  }

  // Add N business days (Mon-Fri) to startDate. Handles startDate falling on
  // a weekend correctly: weekends pass without consuming the counter.
  function addBusinessDays(startDate, days) {
    var d = new Date(startDate.getTime());
    while (days > 0) {
      d.setDate(d.getDate() + 1);
      if (!isWeekend(d)) days--;
    }
    return d;
  }

  function ordinal(n) {
    var suffixes = ['th', 'st', 'nd', 'rd'];
    var v = n % 100;
    return n + (suffixes[(v - 20) % 10] || suffixes[v] || suffixes[0]);
  }

  function fmtRange(d1, d2) {
    var sameMonth = d1.getMonth() === d2.getMonth() &&
                    d1.getFullYear() === d2.getFullYear();
    if (sameMonth) {
      return MONTH_SHORT[d1.getMonth()] + ' ' + ordinal(d1.getDate()) +
             ' - ' + ordinal(d2.getDate());
    }
    return MONTH_SHORT[d1.getMonth()] + ' ' + ordinal(d1.getDate()) + ' - ' +
           MONTH_SHORT[d2.getMonth()] + ' ' + ordinal(d2.getDate());
  }

  // ===========================================================================
  // LOOKUP + CALCULATE
  // ===========================================================================
  function normalize(s) {
    return (s || '').toLowerCase().trim().replace(/\s+/g, ' ');
  }

  function lookupBrand(vendor) {
    var key = normalize(vendor);
    if (BRAND_ALIASES[key]) key = BRAND_ALIASES[key];
    return key in BRAND_TRANSIT ? { key: key, transit: BRAND_TRANSIT[key] } : null;
  }

  function calculate(brand, location) {
    var match = lookupBrand(brand);
    if (!match) return { kind: 'unknown_brand' };
    if (match.transit === null) return { kind: 'made_to_order' };
    if (!location || location.country !== 'CA' || !location.provinceCode) {
      return { kind: 'no_location' };
    }
    var region = PROVINCE_REGION[location.provinceCode];
    if (region === 'territory') {
      return {
        kind: 'territory',
        provinceCode: location.provinceCode,
        city: location.city
      };
    }
    var range = match.transit[region];
    if (!range) return { kind: 'no_location' };

    // Per the shipping-eta page, the chart counts "from the moment you place
    // your order" — so we start the clock right now, no cutoff math.
    var today = new Date();
    today.setHours(0, 0, 0, 0);
    return {
      kind: 'ok',
      provinceCode: location.provinceCode,
      city: location.city,
      postalFSA: location.postalFSA || '',
      from: addBusinessDays(today, range[0]),
      to:   addBusinessDays(today, range[1])
    };
  }

  // ===========================================================================
  // RENDER
  // ===========================================================================
  function setHidden(el, hidden) {
    if (hidden) el.setAttribute('hidden', '');
    else el.removeAttribute('hidden');
    // If the widget is embedded in a host row (e.g. an <li> inside Shopify's
    // shipping-info list), mirror visibility to the row so empty rows don't
    // leave gaps. Opt-in via `data-lc-eta-row` on the parent.
    if (typeof el.closest === 'function') {
      var row = el.closest('[data-lc-eta-row]');
      if (row) {
        if (hidden) row.setAttribute('hidden', '');
        else row.removeAttribute('hidden');
      }
    }
  }

  // Returns the icon span markup, or '' when the host opted out via
  // `data-no-icon` (e.g. when embedding inside a list that already supplies
  // its own icon column).
  function iconHtml(el) {
    return el.hasAttribute('data-no-icon')
      ? ''
      : '<span class="lc-delivery-eta__icon">' + PIN_SVG + '</span>';
  }

  function renderInto(el, eta) {
    // If a popup is anchored to a button inside this widget, close it before
    // we rebuild innerHTML — otherwise the anchor reference becomes detached
    // and the popup is left orphaned in the DOM with broken toggle/scroll.
    if (openPopup && openPopup.anchor && el.contains(openPopup.anchor)) {
      closePopup();
    }
    if (eta.kind === 'ok') {
      var cityOrFsa = eta.city || eta.postalFSA || '';
      var locText = (cityOrFsa ? cityOrFsa + ', ' : '') + eta.provinceCode;
      el.innerHTML =
        iconHtml(el) +
        '<span class="lc-delivery-eta__text">' +
          'Estimated delivery to ' +
          '<button type="button" class="lc-delivery-eta__location" ' +
            'aria-haspopup="dialog" aria-label="Change delivery location"></button>' +
          '<span class="lc-delivery-eta__sep"> : </span>' +
          '<span class="lc-delivery-eta__dates"></span>' +
        '</span>';
      el.querySelector('.lc-delivery-eta__location').textContent = locText;
      el.querySelector('.lc-delivery-eta__dates').textContent = fmtRange(eta.from, eta.to);
      setHidden(el, false);
      return;
    }
    if (eta.kind === 'made_to_order') {
      el.innerHTML =
        iconHtml(el) +
        '<span class="lc-delivery-eta__text">' +
          'Made to order — contact us for delivery time' +
        '</span>';
      setHidden(el, false);
      return;
    }
    // territory (YT/NT/NU — areas we don't serve), unknown_brand, or
    // no_location (outside Canada or province couldn't be resolved) → hide.
    el.textContent = '';
    setHidden(el, true);
  }

  function render(root) {
    var scope = root || document;
    var nodes = scope.querySelectorAll('.lc-delivery-eta');
    if (!nodes.length) return;
    getLocation().then(maybeOverrideForTest).then(function (loc) {
      for (var i = 0; i < nodes.length; i++) {
        var el = nodes[i];
        renderInto(el, calculate(el.getAttribute('data-brand') || '', loc));
      }
    });
  }

  // ===========================================================================
  // POSTAL-CODE POPUP (click city to change delivery location)
  // ===========================================================================

  // First-letter → province fallback. Used when an FSA isn't in the lazy-loaded
  // fsa-cities.json. X is shared between NT/NU so the letter alone can't
  // disambiguate — we pick NT here; the full table resolves correctly for
  // known FSAs.
  var FSA_FIRST_LETTER_TO_PROVINCE = {
    A:'NL', B:'NS', C:'PE', E:'NB', G:'QC', H:'QC', J:'QC',
    K:'ON', L:'ON', M:'ON', N:'ON', P:'ON',
    R:'MB', S:'SK', T:'AB', V:'BC', X:'NT', Y:'YT'
  };

  // Canada Post format. First letter excludes D, F, I, O, Q, U, W, Z.
  // Letters in positions 3/5/6 exclude D, F, I, O, Q, U.
  var POSTAL_REGEX = /^[ABCEGHJ-NPRSTVXY]\d[ABCEGHJ-NPRSTV-Z][ -]?\d[ABCEGHJ-NPRSTV-Z]\d$/i;

  function normalizePostal(raw) {
    return String(raw || '').toUpperCase().replace(/[\s-]+/g, '');
  }
  function fsaFromPostal(raw) {
    var n = normalizePostal(raw);
    return n.length >= 3 ? n.slice(0, 3) : '';
  }

  // Lazy-loaded FSA → { city, province } table. Fetched once, on first popup
  // open, from the URL hinted via `data-lc-fsa-url` on any .lc-delivery-eta
  // element. Falls back to a same-directory relative path if not provided.
  var fsaTablePromise = null;
  function fsaTableUrl() {
    var hint = document.querySelector('.lc-delivery-eta[data-lc-fsa-url]');
    return hint ? hint.getAttribute('data-lc-fsa-url') : FSA_ASSET;
  }
  function loadFsaTable() {
    if (fsaTablePromise) return fsaTablePromise;
    fsaTablePromise = fetch(fsaTableUrl(), { cache: 'force-cache' })
      .then(function (r) {
        // Reset on HTTP failure too, so a transient 404 doesn't cache the
        // empty {} for the rest of the session.
        if (!r.ok) { fsaTablePromise = null; return {}; }
        return r.json();
      })
      .then(function (table) {
        // Strip metadata keys (e.g. _about) so iteration is clean. Direct
        // table[fsa] lookups would already miss them, but this is defensive.
        if (table && typeof table === 'object') {
          for (var k in table) {
            if (k.charAt(0) === '_') delete table[k];
          }
        }
        return table;
      })
      .catch(function () { fsaTablePromise = null; return {}; });
    return fsaTablePromise;
  }

  // Resolve postal → { provinceCode, city, postalFSA }. Uses the table when
  // available; falls back to first-letter province with empty city.
  function resolveFromPostal(raw) {
    var fsa = fsaFromPostal(raw);
    var first = fsa.charAt(0).toUpperCase();
    var fallbackProvince = FSA_FIRST_LETTER_TO_PROVINCE[first] || null;
    return loadFsaTable().then(function (table) {
      var entry = table && table[fsa];
      if (entry && entry.province) {
        return { provinceCode: entry.province, city: entry.city || '', postalFSA: fsa };
      }
      return { provinceCode: fallbackProvince, city: '', postalFSA: fsa };
    });
  }

  function closePopup() {
    if (!openPopup) return;
    var p = openPopup;
    // Remove listeners before clearing openPopup, so any listener that ever
    // checks `if (!openPopup) return;` sees a consistent state.
    document.removeEventListener('mousedown', p._onDocMouseDown, true);
    document.removeEventListener('keydown',   p._onKeyDown,      true);
    window.removeEventListener('resize',      p._onResize);
    window.removeEventListener('scroll',      p._onResize, true);
    openPopup = null;
    if (p.node && p.node.parentNode) p.node.parentNode.removeChild(p.node);
  }

  function positionPopup(node, anchor) {
    var rect = anchor.getBoundingClientRect();
    var scrollX = window.pageXOffset || document.documentElement.scrollLeft;
    var scrollY = window.pageYOffset || document.documentElement.scrollTop;
    var width = node.offsetWidth || 280;
    var viewportRight = scrollX + document.documentElement.clientWidth;
    var left = rect.left + scrollX;
    if (left + width > viewportRight - 16) left = viewportRight - width - 16;
    if (left < scrollX + 8) left = scrollX + 8;
    node.style.top  = (rect.bottom + scrollY + 8) + 'px';
    node.style.left = left + 'px';
  }

  function showPopup(anchor) {
    closePopup();
    var existing = readOverride();
    var node = document.createElement('div');
    node.className = 'lc-eta-popup';
    node.setAttribute('role', 'dialog');
    node.setAttribute('aria-label', 'Change delivery location');
    node.innerHTML =
      '<h3 class="lc-eta-popup__title">Enter postal code</h3>' +
      '<input class="lc-eta-popup__input" type="text" maxlength="7" ' +
        'autocomplete="postal-code" inputmode="text" placeholder="e.g. V6A1C7" ' +
        'aria-label="Postal code" />' +
      '<p class="lc-eta-popup__error" aria-live="polite"></p>' +
      '<button type="button" class="lc-eta-popup__submit">Update</button>' +
      '<button type="button" class="lc-eta-popup__reset"' +
        (existing ? '' : ' hidden') + '>Use my location instead</button>';
    document.body.appendChild(node);
    positionPopup(node, anchor);

    var input  = node.querySelector('.lc-eta-popup__input');
    var error  = node.querySelector('.lc-eta-popup__error');
    var submit = node.querySelector('.lc-eta-popup__submit');
    var reset  = node.querySelector('.lc-eta-popup__reset');

    if (existing && existing.postalFSA) input.value = existing.postalFSA;
    setTimeout(function () { input.focus(); input.select(); }, 0);

    function attempt() {
      if (submit.disabled) return; // a lookup is already in flight
      var raw = input.value;
      if (!POSTAL_REGEX.test(raw)) {
        input.classList.add('is-invalid');
        error.textContent = 'Please enter a valid Canadian postal code.';
        return;
      }
      input.classList.remove('is-invalid');
      error.textContent = '';
      submit.disabled = true;
      resolveFromPostal(raw).then(function (loc) {
        if (!loc.provinceCode) {
          error.textContent = 'We couldn’t recognize that postal code.';
          submit.disabled = false;
          return;
        }
        // Block postal codes in areas we don't serve (YT/NT/NU). Without
        // this guard the override would save, the popup would close, and the
        // widget would hide silently — leaving the customer with no obvious
        // way to recover.
        if (PROVINCE_REGION[loc.provinceCode] === 'territory') {
          error.innerHTML = 'Please <a href="/pages/contact-us">contact us</a> for a shipping quote.';
          submit.disabled = false;
          return;
        }
        writeOverride(loc);
        closePopup();
        render(document);
      });
    }

    submit.addEventListener('click', attempt);
    input.addEventListener('keydown', function (e) {
      if (e.key === 'Enter') { e.preventDefault(); attempt(); }
    });
    reset.addEventListener('click', function () {
      clearOverride();
      ipLocationPromise = null; // refresh from cache/IP next time
      closePopup();
      render(document);
    });

    function onDocMouseDown(e) {
      if (node.contains(e.target)) return;
      if (anchor.contains(e.target)) return; // anchor click toggles, handled elsewhere
      closePopup();
    }
    function onKeyDown(e) { if (e.key === 'Escape') closePopup(); }
    function onResize()   { positionPopup(node, anchor); }

    document.addEventListener('mousedown', onDocMouseDown, true);
    document.addEventListener('keydown',   onKeyDown,      true);
    window.addEventListener('resize',      onResize);
    window.addEventListener('scroll',      onResize, true);

    openPopup = {
      node: node,
      anchor: anchor,
      _onDocMouseDown: onDocMouseDown,
      _onKeyDown: onKeyDown,
      _onResize: onResize
    };
  }

  // Delegated handler so re-rendered widgets stay interactive. Clicking the
  // same anchor while the popup is open toggles it closed; clicking a
  // different widget's anchor swaps the popup over to that one.
  document.addEventListener('click', function (e) {
    var t = e.target;
    if (!t || typeof t.closest !== 'function') return;
    var loc = t.closest('.lc-delivery-eta__location');
    if (!loc || !loc.closest('.lc-delivery-eta')) return;
    e.preventDefault();
    if (openPopup && openPopup.anchor === loc) {
      closePopup();
      return;
    }
    showPopup(loc);
  });

  // ===========================================================================
  // INIT
  // ===========================================================================
  // Inject a stylesheet that:
  //   1. Forces hidden widgets and their host rows to collapse, even when the
  //      host row has an inline `display:flex` set in Liquid.
  //   2. Resets the <button> we use for the clickable location so it inherits
  //      surrounding text styling.
  //   3. Styles the postal-code popover.
  (function injectStyle() {
    if (document.getElementById('lc-delivery-eta-style')) return;
    var style = document.createElement('style');
    style.id = 'lc-delivery-eta-style';
    style.textContent = [
      '.lc-delivery-eta[hidden],[data-lc-eta-row][hidden]{display:none !important;}',
      '.lc-delivery-eta__location{background:none;border:0;padding:0;margin:0;',
        'font:inherit;color:inherit;cursor:pointer;}',
      '.lc-delivery-eta__location:focus-visible{outline:2px solid #151414;',
        'outline-offset:2px;border-radius:2px;}',
      '.lc-eta-popup{position:absolute;z-index:1000;background:#fff;',
        'box-shadow:0 12px 32px rgba(0,0,0,.18),',
        '0 2px 6px rgba(0,0,0,.08);padding:16px;width:280px;',
        'box-sizing:border-box;font-family:inherit;color:#151414;}',
      '.lc-eta-popup__title{font-size:14px;font-weight:700;margin:0 0 10px;}',
      '.lc-eta-popup__input{width:100%;box-sizing:border-box;padding:10px 12px;',
        'font-size:14px;border:1px solid #d6d6d6;border-radius:6px;outline:none;',
        'text-transform:uppercase;letter-spacing:.04em;}',
      '.lc-eta-popup__input:focus{border-color:#151414;}',
      '.lc-eta-popup__input.is-invalid{border-color:#c0392b;}',
      '.lc-eta-popup__error{font-size:12px;color:#c0392b;margin:6px 0 0;min-height:1em;}',
      '.lc-eta-popup__error a{color:inherit;text-decoration:underline;}',
      '.lc-eta-popup__submit{display:block;width:100%;margin-top:12px;padding:12px;',
        'font-size:13px;font-weight:700;letter-spacing:.06em;text-transform:uppercase;',
        'background:#151414;color:#fff;border:0;cursor:pointer;}',
      '.lc-eta-popup__submit:hover:not([disabled]){background:#000;}',
      '.lc-eta-popup__submit[disabled]{opacity:.6;cursor:wait;}',
      '.lc-eta-popup__reset{display:block;margin:10px auto 0;font-size:12px;',
        'color:#6b6b6b;background:none;border:0;padding:0;cursor:pointer;',
        'text-decoration:underline;}',
      '.lc-eta-popup__reset[hidden]{display:none;}'
    ].join('');
    (document.head || document.documentElement).appendChild(style);
  })();

  // Kick off the location lookup ASAP on every page so the answer is in
  // localStorage by the time the customer hits a product page.
  getLocation();

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { render(); });
  } else {
    render();
  }

  // Public API for AJAX-driven section reloads (Dawn-style themes, quick views).
  window.LCDeliveryETA = {
    render: render,
    calculate: function (brand) {
      return getLocation().then(maybeOverrideForTest).then(function (loc) { return calculate(brand, loc); });
    },
    clearCache: function () { try { localStorage.removeItem(CACHE_KEY); } catch (_) {} }
  };
})();
